// API 연동 컴포넌트
// src/pages/admin/components/draw/ApiSyncer.tsx

interface ApiSyncerProps {
  apiStartRound: string;
  apiEndRound: string;
  apiProgress: string;
  apiRunning: boolean;
  retryRounds: number[];
  onStartRoundChange: (value: string) => void;
  onEndRoundChange: (value: string) => void;
  onFetch: () => void;
  onAbort: () => void;
  onRetryFailedRounds: () => void;
}

const ranges = [
  { label: '1~855회차 (전체)', start: '1', end: '855' },
  { label: '1~285회차', start: '1', end: '285' },
  { label: '286~570회차', start: '286', end: '570' },
  { label: '571~855회차', start: '571', end: '855' },
];

export default function ApiSyncer({
  apiStartRound,
  apiEndRound,
  apiProgress,
  apiRunning,
  retryRounds,
  onStartRoundChange,
  onEndRoundChange,
  onFetch,
  onAbort,
  onRetryFailedRounds,
}: ApiSyncerProps) {
  return (
    <div className="space-y-6">
      <div className="bg-background-50 rounded-2xl border border-background-200 p-6">
        <h4 className="font-heading text-sm text-foreground-700 mb-2">동행복권 공식 API로 데이터 가져오기</h4>
        <p className="text-xs text-foreground-400 mb-5">
          서버사이드 Edge Function을 통해 동행복권 API를 안전하게 호출하여 과거 회차별 당첨번호를 가져옵니다.<br />
          <strong className="text-secondary-600">대량 범위(전체 855회차 이상)는 50회차씩 자동 분할 호출됩니다.</strong>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">시작 회차</label>
            <input
              type="number"
              value={apiStartRound}
              onChange={(e) => onStartRoundChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-foreground-500 mb-1.5">종료 회차</label>
            <input
              type="number"
              value={apiEndRound}
              onChange={(e) => onEndRoundChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {ranges.map((r) => (
            <button
              key={r.label}
              onClick={() => {
                onStartRoundChange(r.start);
                onEndRoundChange(r.end);
              }}
              className="px-3 py-1.5 rounded-lg text-xs border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onFetch}
            disabled={apiRunning}
            className="px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {apiRunning ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                가져오는 중...
              </span>
            ) : (
              'API에서 가져오기'
            )}
          </button>
          {apiRunning && (
            <button
              onClick={onAbort}
              className="px-4 py-2.5 rounded-full bg-accent-100 hover:bg-accent-200 text-accent-700 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              중단 (현재 청크 완료 후 정지)
            </button>
          )}
        </div>

        {apiProgress && (
          <div className="mt-3 p-3 rounded-lg bg-background-100 border border-background-200">
            <p className="text-sm text-foreground-600 flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></span>
              {apiProgress}
            </p>
          </div>
        )}

        {/* 실패 회차 재시도 */}
        {retryRounds.length > 0 && !apiRunning && (
          <div className="mt-4 p-4 rounded-xl bg-accent-50 border border-accent-200">
            <div className="flex items-start gap-2 mb-3">
              <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
              <div>
                <p className="text-sm text-accent-700 font-medium">실패한 회차 {retryRounds.length}건</p>
                <p className="text-xs text-accent-600 mt-0.5 break-all">
                  {retryRounds.slice(0, 30).join(', ')}
                  {retryRounds.length > 30 ? ` … 외 ${retryRounds.length - 30}건` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onRetryFailedRounds}
              className="px-4 py-2 rounded-lg bg-accent-500 hover:bg-accent-600 text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              실패한 회차만 재시도
            </button>
          </div>
        )}
      </div>
    </div>
  );
}